'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { FC, ReactElement } from 'react';
import { BaseFillFolder } from '@/app/components/icons/BaseFillFolder';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineMarket } from '@/app/components/icons/BaseLineMarket';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { ProductLineMark } from '@/app/components/icons/ProductLineMark';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { Avatar } from '@/app/components/shared/UserAvatar';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Input } from '@/app/components/ui/input';
import { SITE, TEXT_2_AUDIO_IMAGE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { cn, getAudioImageIndex } from '@/app/utils';
import { useDownload } from '../_hooks/use-download';
import { useRenameFile } from '../_hooks/use-rename-file';
import { type FileOperation, type FileOperationType, getContextOperations } from '../_utils/file-operations-config';
import type { FileItem } from '../types';
import { AudioCard } from './AudioCard';
import { DraggableItem } from './DraggableItem';
import { FileContextMenu } from './FileContextMenu';
import { VideoCard } from './VideoCard';

interface FileGridItemProps {
  item: FileItem;
  isSelected: boolean;
  isDraggingAny: boolean;
  moving?: boolean;
  selectedItems: string[];
  files: FileItem[];
  isOperationLoading: FileOperationType | null;
  onFileClick: (itemId: string, event?: React.MouseEvent) => void;
  onSelectItem: (itemId: string) => void;
  onOperation: (operation: FileOperationType, item: FileItem | FileItem[]) => void;
  // 文件状态更新回调
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
}

export const FileGridItem: FC<FileGridItemProps> = ({
  item,
  isSelected,
  isDraggingAny,
  moving = false,
  selectedItems,
  files,
  isOperationLoading,
  onFileClick,
  onSelectItem,
  onOperation,
  onFilesUpdate,
}) => {
  const pathname = usePathname();
  const { getDownloadMenuItems } = useDownload();
  const t = useTranslations();
  const isTeamSpace = useStore((state) => state.isTeamSpace);
  const {
    isEditingName,
    newName,
    renamingFile,
    editingRef,
    handleEditName,
    handleNameChange,
    handleKeyDown,
    handleRename,
  } = useRenameFile({ item, onFilesUpdate });

  const isSelectedInMultiSelect = isSelected && selectedItems.length > 1;
  const showAssetWatermark = SITE === 'global';

  // 渲染下载菜单项（带水印项用锚点以满足 GPT 插页「点击锚点」触发展示）
  const renderDownloadMenuItems = (operation: FileOperation) => {
    const downloadMenuItems = getDownloadMenuItems(item, operation.icon);

    return downloadMenuItems.map((menuItem) => {
      const content = (
        <>
          <menuItem.icon className="mr-2 text-base" />
          <div>
            <div>{menuItem.label}</div>
            {menuItem.badge && (
              <span className="ml-1 scale-[0.8] rounded-[100px] bg-system-blue02 px-2 py-0.5 text-white text-xs">
                {menuItem.badge}
              </span>
            )}
            {menuItem.disabled && menuItem.disabledTip && (
              <div className="text-system-white64 text-xs">{menuItem.disabledTip}</div>
            )}
          </div>
        </>
      );
      if (menuItem.renderAsAnchor) {
        return (
          <DropdownMenuItem
            key={menuItem.key}
            asChild
            className="px-2 py-3 hover:bg-system-white10 aria-selected:bg-system-white10"
            disabled={menuItem.disabled}
          >
            <a
              href={pathname ?? '/'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!menuItem.disabled) menuItem.onClick();
              }}
              className="flex cursor-pointer select-none items-center outline-none"
            >
              {content}
            </a>
          </DropdownMenuItem>
        );
      }
      return (
        <DropdownMenuItem
          key={menuItem.key}
          className="px-2 py-3 hover:bg-system-white10 aria-selected:bg-system-white10"
          onClick={(e) => {
            e.stopPropagation();
            if (!menuItem.disabled) menuItem.onClick();
          }}
          disabled={menuItem.disabled}
        >
          {content}
        </DropdownMenuItem>
      );
    });
  };

  // 渲染下拉菜单项
  const renderDropdownOperations = (item: FileItem) => {
    const contextOperations = getContextOperations(false, item.type === 'folder'); // More按钮始终是单选模式

    const elements: ReactElement[] = [];

    for (let i = 0; i < contextOperations.length; i++) {
      const operation = contextOperations[i];
      const Icon = operation.icon;
      const isCurrentLoading = isOperationLoading === operation.id;

      // 特殊处理 download 操作
      if (operation.id === 'download') {
        elements.push(...renderDownloadMenuItems(operation));
        continue;
      }

      // 添加菜单项
      elements.push(
        <DropdownMenuItem
          key={operation.id}
          className="px-2 py-3 hover:bg-system-white10 aria-selected:bg-system-white10"
          onClick={(e) => {
            e.stopPropagation();
            // 如果是 rename 操作，直接调用 handleEditName
            if (operation.id === 'rename') {
              handleEditName();
            } else {
              onOperation(operation.id, item);
            }
          }}
          disabled={isCurrentLoading}
        >
          {isCurrentLoading ? (
            <BaseLineLoading2 className="mr-2 animate-spin text-base" />
          ) : (
            <Icon className="mr-2 text-base" />
          )}
          {t(`creations.operations.${operation.id}`)}
        </DropdownMenuItem>,
      );
    }

    return elements;
  };

  // 渲染文件内容区域
  const renderFileContent = () => {
    if (item.type === 'folder') {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <BaseFillFolder className="h-20 w-20 text-system-white24" />
        </div>
      );
    }

    if ((item.creationType === 'video' || item.creationType === 'mini_video') && item.coverUrl && item.contentUrl) {
      return <VideoCard videoUrl={item.contentUrl} posterUrl={item.coverUrl} />;
    }

    if (item.creationType === 'image') {
      return (
        <div className="relative h-full w-full">
          <ImageWithPlaceholder
            src={item.coverUrl}
            alt={item.name}
            className="h-full w-full object-contain"
            placeHolderClassName="bg-system-white10"
          />
          {showAssetWatermark && <MediaWithWatermark overlay width={16} height={9} showWatermark />}
        </div>
      );
    }

    if (item.creationType === 'audio') {
      return (
        <AudioCard
          audioImage={TEXT_2_AUDIO_IMAGE[getAudioImageIndex(item.id, TEXT_2_AUDIO_IMAGE.length)]}
          prompt={item.audioText}
        />
      );
    }
  };

  return (
    <DraggableItem
      item={item}
      isSelected={isSelected}
      isDraggingAny={isDraggingAny}
      droppable={item.type === 'folder'}
      disabled={moving}
    >
      <FileContextMenu
        item={item}
        isLoading={isOperationLoading}
        onOperation={(operation, item) => {
          // 如果多选且选中当前item，则是针对多选的文件进行操作
          if (isSelectedInMultiSelect) {
            const selectedFiles = files.filter((file) => selectedItems.includes(file.id));
            onOperation(operation, selectedFiles);
            return;
          }

          if (operation === 'rename') {
            handleEditName();
            return;
          }

          onOperation(operation, item);
        }}
        isBatch={isSelectedInMultiSelect}
      >
        <div data-item-id={item.id} data-no-selection className="group relative cursor-pointer transition-all">
          <div
            className="relative aspect-video w-full overflow-hidden rounded-8 bg-system-white10"
            onClick={(event) => onFileClick(item.id, event)}
          >
            {renderFileContent()}
            {moving && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40">
                <BaseLineLoading2 className="animate-spin text-2xl text-system-white" />
              </div>
            )}
          </div>

          <CheckboxFill
            checked={isSelected}
            onCheckedChange={() => onSelectItem(item.id)}
            className={cn(
              'absolute top-2 left-2 z-10 scale-75 text-xl opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 [&_rect]:fill-black',
              selectedItems.length > 0 && 'scale-100 opacity-100',
            )}
          />

          <div className="absolute top-2 right-2 z-10 flex items-center space-x-1">
            <button
              className="rounded-6 bg-system-black56 p-1.5 hover:bg-system-black64"
              onClick={() => onOperation('mark', item)}
            >
              {item.is_favorite ? (
                <BaseLineMarket className="text-[#FFCC00] text-base" />
              ) : (
                <ProductLineMark className="text-base text-system-white" />
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={isSelectedInMultiSelect}>
                <button
                  className={cn(
                    'rounded-6 bg-system-black56 p-1.5 hover:bg-system-black64',
                    isSelectedInMultiSelect && 'cursor-not-allowed opacity-60 hover:bg-system-black56',
                  )}
                  type="button"
                  aria-disabled={isSelectedInMultiSelect}
                  disabled={isSelectedInMultiSelect}
                >
                  <BaseLineMore className="text-base text-system-white" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-fit border border-system-white12 bg-[#1A1A1A]"
                align="start"
                side="right"
              >
                {renderDropdownOperations(item)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-2 flex items-center justify-between gap-1 pr-4">
            {isEditingName ? (
              <div ref={editingRef} className="flex-1">
                <Input
                  autoFocus
                  className="h-fit flex-1 rounded-[4px] border border-[#343434] bg-[#20232A] px-4 py-2 font-medium text-sm text-system-white" // 直接用 rounded-4 无法覆盖 rounded-8，twMerge 没合并 rounded
                  value={newName}
                  disabled={renamingFile}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleRename}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <p
                className="flex-1 cursor-pointer truncate rounded-4 border border-transparent px-4 py-2 font-medium text-sm text-system-white hover:border-[#343434] hover:bg-[#20232A]"
                onClick={handleEditName}
              >
                <CustomTooltip
                  content={item.name}
                  contentClassName="[word-break:break-word]"
                  showOnlyOnOverflow
                  maxLines={1}
                >
                  {item.name}
                </CustomTooltip>
              </p>
            )}
            {isTeamSpace && item.owner_id && (
              <Avatar
                id={item.owner_id}
                nickname={item.owner_nickname}
                url={item.owner_avatar}
                className="mr-1 size-5"
                nameClassName="text-[10px]"
              />
            )}
            {item.size && <p className="ml-2 flex-shrink-0 text-system-white64 text-xs">{item.size}</p>}
          </div>
        </div>
      </FileContextMenu>
    </DraggableItem>
  );
};
